import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { StickerSkate, StickerStar } from "./Stickers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Guestbook = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", message: "" });

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/guestbook`);
      setEntries(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Name and message required, surfer.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/guestbook`, form);
      toast.success("Signed! Welcome to the wall.");
      setForm({ name: "", location: "", message: "" });
      load();
    } catch (e) {
      toast.error("Couldn't sign right now. Try again in a sec.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-[#C0C0C0] border-y-4 border-black py-16 px-6 relative" data-testid="guestbook-section">
      <Toaster position="top-center" richColors />
      <div className="absolute top-6 right-8 hidden md:block"><StickerSkate size={130} rotate={10} className="float-slow" /></div>
      <div className="absolute -top-6 left-10 hidden md:block"><StickerStar size={70} rotate={-20} className="float-med" /></div>

      <div className="max-w-5xl mx-auto">
        {/* Win95-style window */}
        <div className="bg-[#E0E0E0] border-t-4 border-l-4 border-white border-b-4 border-r-4 border-black shadow-[10px_10px_0_#000]">
          <div className="bg-[#000080] text-white flex items-center justify-between px-3 py-1 font-pixel text-lg">
            <span>★ guestbook.exe :: leave a note ★</span>
            <span className="flex gap-1">
              <span className="bg-[#C0C0C0] text-black border-2 border-white px-2">_</span>
              <span className="bg-[#C0C0C0] text-black border-2 border-white px-2">□</span>
              <span className="bg-[#C0C0C0] text-black border-2 border-white px-2">X</span>
            </span>
          </div>

          <div className="p-6 sm:p-8">
            <h3 className="font-display text-4xl tracking-wider text-black mb-2">SIGN THE WALL</h3>
            <p className="font-pixel text-xl text-black/80 mb-6">
              Say hi · drop encouragement · tell me where you're reading from
            </p>

            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="font-pixel text-xl bg-white border-4 border-black px-4 py-3 outline-none focus:bg-[#FFFEC2]"
                data-testid="guestbook-name"
              />
              <input
                type="text"
                placeholder="where from? (optional)"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="font-pixel text-xl bg-white border-4 border-black px-4 py-3 outline-none focus:bg-[#FFFEC2]"
                data-testid="guestbook-location"
              />
              <textarea
                placeholder="your message..."
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="sm:col-span-2 font-pixel text-xl bg-white border-4 border-black px-4 py-3 outline-none resize-none focus:bg-[#FFFEC2]"
                data-testid="guestbook-message"
              />
              <button
                type="submit"
                disabled={submitting}
                className="sm:col-span-2 font-display tracking-wider text-2xl uppercase px-6 py-3 bg-[#F87171] text-white border-4 border-black shadow-[6px_6px_0_#000] btn-press disabled:opacity-60"
                data-testid="guestbook-submit"
              >
                {submitting ? "Saving..." : "Sign the guestbook →"}
              </button>
            </form>
          </div>

          <div className="border-t-4 border-black bg-[#FFF4D6] p-6 sm:p-8 max-h-[420px] overflow-y-auto">
            <div className="font-display tracking-wider text-2xl mb-4 text-black">RECENT SIGNATURES</div>
            {loading ? (
              <div className="font-pixel text-xl">Loading <span className="blink">_</span></div>
            ) : entries.length === 0 ? (
              <div className="font-pixel text-xl text-black/70">
                No signatures yet. Be the first to sign the wall.
              </div>
            ) : (
              <ul className="space-y-4" data-testid="guestbook-entries">
                {entries.map((e) => (
                  <li key={e.id} className="bg-white border-4 border-black p-4 shadow-[4px_4px_0_#000]">
                    <div className="flex flex-wrap items-baseline gap-3">
                      <span className="font-display text-2xl tracking-wider text-[#F87171]">{e.name}</span>
                      {e.location && <span className="font-pixel text-base text-black/70">from {e.location}</span>}
                      <span className="font-pixel text-base text-black/50 ml-auto">
                        {new Date(e.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="font-body text-base mt-2 whitespace-pre-wrap">{e.message}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Guestbook;
